import { useEffect, useRef, useState, useCallback } from "react";
import { LEVELS } from "@/data/levels";
import {
  SFX,
  startMusic,
  stopMusic,
  setMusicEnabled,
  isMusicEnabled,
  unlockAudio,
} from "@/lib/audio";
import { Heart, Volume2, VolumeX, Pause, Play, Star } from "lucide-react";
import CertificateModal from "@/components/game/CertificateModal";
import { toast } from "sonner";
import axios from "axios";
import {
  VIEW_W,
  VIEW_H,
  GROUND_Y,
  STATE,
  drawBackground,
  drawTiger,
  drawHunter,
  drawPickup,
} from "@/components/game/drawing";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Constants
const GRAVITY = 0.7;
const JUMP_VELOCITY = -13;
const SCROLL_SPEED = 4;
const LEVEL_DURATION_MS = 35000; // 35s per level
const HUNTER_INTERVAL_MS = 2200;
const PICKUP_INTERVAL_MS = 1600;

function rand(min, max) {
  return Math.random() * (max - min) + min;
}


export default function TigerGame() {
  const canvasRef = useRef(null);
  const stateRef = useRef(null);
  const rafRef = useRef(null);
  const keysRef = useRef({});
  const [hud, setHud] = useState({
    lives: 5,
    score: 0,
    levelIdx: 0,
    timeLeft: LEVEL_DURATION_MS,
    paused: false,
    musicOn: true,
  });
  const [overlay, setOverlay] = useState({ kind: "start" }); // start | levelComplete | gameOver | finished
  const [playerName, setPlayerName] = useState(
    () => localStorage.getItem("tiger_player_name") || "",
  );
  const [askName, setAskName] = useState(
    () => !localStorage.getItem("tiger_player_name"),
  );
  const [showCertificate, setShowCertificate] = useState(false);
  const [lastResult, setLastResult] = useState({ stars: 0, levelName: "" });

  const initLevel = useCallback((levelIdx) => {
    stateRef.current = {
      tiger: {
        x: 160,
        y: GROUND_Y - 26,
        vy: 0,
        onGround: true,
        state: STATE.RUN,
        dir: 1,
        frame: 0,
        canDoubleJump: true,
        flipAngle: 0,
        actionUntil: 0,
        invincibleUntil: 0,
      },
      hunters: [],
      pickups: [],
      scrollX: 0,
      lastHunter: 0,
      lastPickup: 0,
      startTime: performance.now(),
      meat: 0,
      water: 0,
      hits: 0,
    };
  }, []);

  const startGame = useCallback(() => {
    unlockAudio();
    setHud((h) => ({
      ...h,
      lives: 5,
      score: 0,
      levelIdx: 0,
      timeLeft: LEVEL_DURATION_MS,
      paused: false,
    }));
    initLevel(0);
    setOverlay({ kind: null });
    if (isMusicEnabled()) startMusic(LEVELS[0].music);
  }, [initLevel]);

  const goToLevel = useCallback(
    (idx) => {
      if (idx >= LEVELS.length) {
        setOverlay({ kind: "finished" });
        stopMusic();
        SFX.win();
        return;
      }
      setHud((h) => ({
        ...h,
        levelIdx: idx,
        timeLeft: LEVEL_DURATION_MS,
        paused: false,
      }));
      initLevel(idx);
      setOverlay({ kind: null });
      if (isMusicEnabled()) startMusic(LEVELS[idx].music);
    },
    [initLevel],
  );

  // Game loop
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const loop = (t) => {
      const s = stateRef.current;
      const level = LEVELS[hud.levelIdx];
      if (!s || !level || hud.paused || overlay.kind) {
        // Even when paused/overlay, keep rendering current frame
        if (s && level) {
          drawBackground(ctx, level, s.scrollX);
          s.pickups.forEach((p) => drawPickup(ctx, p, t));
          s.hunters.forEach((h) => drawHunter(ctx, h));
          drawTiger(ctx, s.tiger, t);
        }
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      const tiger = s.tiger;
      // Input handling
      const k = keysRef.current;
      const inAction =
        tiger.actionUntil > t &&
        (tiger.state === STATE.EAT ||
          tiger.state === STATE.DRINK ||
          tiger.state === STATE.ROAR ||
          tiger.state === STATE.BACKFLIP);

      if (!inAction && tiger.state !== STATE.HURT) {
        tiger.state = tiger.onGround ? STATE.RUN : STATE.JUMP;
      }

      // Jump
      if ((k.ArrowUp || k.KeyW || k.Space) && tiger.onGround && !inAction) {
        tiger.vy = JUMP_VELOCITY;
        tiger.onGround = false;
        tiger.canDoubleJump = true;
        SFX.jump();
        // Consume one tap
        k.ArrowUp = false;
        k.KeyW = false;
        k.Space = false;
      }

      // Apply gravity
      tiger.vy += GRAVITY;
      tiger.y += tiger.vy;
      if (tiger.y >= GROUND_Y - 26) {
        tiger.y = GROUND_Y - 26;
        tiger.vy = 0;
        tiger.onGround = true;
        if (tiger.state === STATE.BACKFLIP) {
          tiger.flipAngle = 0;
        }
      }

      // Backflip rotation
      if (tiger.state === STATE.BACKFLIP) {
        tiger.flipAngle -= 0.22;
      }

      // Frame counter
      tiger.frame++;

      // Scroll
      s.scrollX += SCROLL_SPEED;

      // Spawn hunters
      if (t - s.lastHunter > HUNTER_INTERVAL_MS) {
        s.lastHunter = t;
        s.hunters.push({
          x: VIEW_W + 60,
          y: GROUND_Y - 30,
          vx: -SCROLL_SPEED - rand(0.5, 2),
        });
      }
      // Spawn pickups
      if (t - s.lastPickup > PICKUP_INTERVAL_MS) {
        s.lastPickup = t;
        const type = Math.random() > 0.5 ? "meat" : "water";
        s.pickups.push({
          x: VIEW_W + 40,
          y: GROUND_Y - 60 - Math.random() * 80,
          type,
          vx: -SCROLL_SPEED,
        });
      }

      // Update hunters
      s.hunters.forEach((h) => (h.x += h.vx));
      s.hunters = s.hunters.filter((h) => h.x > -60);

      // Update pickups
      s.pickups.forEach((p) => (p.x += p.vx));
      s.pickups = s.pickups.filter((p) => p.x > -40);

      // Collisions: hunters
      const tigerBox = {
        x: tiger.x - 26,
        y: tiger.y - 26,
        w: 56,
        h: 50,
      };
      s.hunters.forEach((h, i) => {
        const hb = { x: h.x - 18, y: h.y - 60, w: 50, h: 70 };
        if (
          tigerBox.x < hb.x + hb.w &&
          tigerBox.x + tigerBox.w > hb.x &&
          tigerBox.y < hb.y + hb.h &&
          tigerBox.y + tigerBox.h > hb.y &&
          t > tiger.invincibleUntil &&
          tiger.state !== STATE.ROAR
        ) {
          // Hit!
          s.hits++;
          tiger.invincibleUntil = t + 1500;
          tiger.state = STATE.HURT;
          tiger.actionUntil = t + 400;
          SFX.hit();
          setHud((h2) => {
            const nl = h2.lives - 1;
            if (nl <= 0) {
              stopMusic();
              SFX.lose();
              setOverlay({ kind: "gameOver" });
            }
            return { ...h2, lives: nl };
          });
        }
        // If roaring, scare hunter away
        if (
          tiger.state === STATE.ROAR &&
          Math.abs(h.x - tiger.x) < 200 &&
          !h.scared
        ) {
          h.vx = 8;
          h.scared = true;
        }
      });

      // Collisions: pickups
      s.pickups = s.pickups.filter((p) => {
        const pb = { x: p.x - 12, y: p.y - 12, w: 24, h: 24 };
        const hit =
          tigerBox.x < pb.x + pb.w &&
          tigerBox.x + tigerBox.w > pb.x &&
          tigerBox.y < pb.y + pb.h &&
          tigerBox.y + tigerBox.h > pb.y;
        if (hit) {
          if (p.type === "meat") {
            s.meat++;
            tiger.state = STATE.EAT;
            tiger.actionUntil = t + 280;
            SFX.eat();
          } else {
            s.water++;
            tiger.state = STATE.DRINK;
            tiger.actionUntil = t + 280;
            SFX.drink();
          }
          setHud((h2) => ({ ...h2, score: h2.score + 10 }));
          return false;
        }
        return true;
      });

      // Time
      const elapsed = t - s.startTime;
      const remaining = Math.max(0, LEVEL_DURATION_MS - elapsed);
      if (Math.floor(remaining / 100) !== Math.floor(hud.timeLeft / 100)) {
        setHud((h2) => ({ ...h2, timeLeft: remaining }));
      }
      if (remaining <= 0) {
        // Level complete
        const stars = computeStars(s);
        setLastResult({ stars, levelName: level.name });
        stopMusic();
        SFX.win();
        setOverlay({ kind: "levelComplete", stars, levelName: level.name });
        rafRef.current = requestAnimationFrame(loop);
        return;
      }

      // Draw
      drawBackground(ctx, level, s.scrollX);
      s.pickups.forEach((p) => drawPickup(ctx, p, t));
      s.hunters.forEach((h) => drawHunter(ctx, h));

      // Flicker tiger when invincible
      if (t < tiger.invincibleUntil && Math.floor(t / 80) % 2 === 0) {
        // skip drawing tiger this frame
      } else {
        drawTiger(ctx, tiger, t);
      }

      rafRef.current = requestAnimationFrame(loop);
    };

    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, [hud.levelIdx, hud.paused, overlay.kind, hud.timeLeft]);

  // Keyboard
  useEffect(() => {
    const onDown = (e) => {
      // Prevent scrolling on game keys
      if (
        ["ArrowUp", "ArrowDown", "ArrowLeft", "ArrowRight", "Space"].includes(
          e.code,
        )
      ) {
        e.preventDefault();
      }
      keysRef.current[e.code] = true;

      const s = stateRef.current;
      if (!s || hud.paused || overlay.kind) return;
      const tiger = s.tiger;
      const t = performance.now();

      // Double jump for backflip
      if (
        (e.code === "ArrowUp" || e.code === "KeyW") &&
        !tiger.onGround &&
        tiger.canDoubleJump
      ) {
        tiger.vy = JUMP_VELOCITY * 0.85;
        tiger.canDoubleJump = false;
        tiger.state = STATE.BACKFLIP;
        tiger.actionUntil = t + 500;
        SFX.backflip();
      }
      // Eat
      if (e.code === "KeyE") {
        tiger.state = STATE.EAT;
        tiger.actionUntil = t + 300;
        SFX.eat();
      }
      // Drink
      if (e.code === "KeyD") {
        tiger.state = STATE.DRINK;
        tiger.actionUntil = t + 300;
        SFX.drink();
      }
      // Roar
      if (e.code === "KeyR") {
        tiger.state = STATE.ROAR;
        tiger.actionUntil = t + 700;
        SFX.roar();
      }
      // Pause
      if (e.code === "KeyP" || e.code === "Escape") {
        setHud((h) => ({ ...h, paused: !h.paused }));
      }
    };
    const onUp = (e) => {
      keysRef.current[e.code] = false;
    };
    window.addEventListener("keydown", onDown);
    window.addEventListener("keyup", onUp);
    return () => {
      window.removeEventListener("keydown", onDown);
      window.removeEventListener("keyup", onUp);
    };
  }, [hud.paused, overlay.kind]);

  // Submit game score to backend whenever a level completes
  useEffect(() => {
    if (overlay.kind !== "levelComplete") return;
    if (!playerName) return;
    const level = LEVELS[hud.levelIdx];
    if (!level) return;
    axios
      .post(`${API}/game/score`, {
        name: playerName,
        level_index: hud.levelIdx,
        level_name: level.name,
        stars: overlay.stars ?? 0,
        score: hud.score,
      })
      .catch((err) => {
        console.error("Failed to save game score", err);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlay.kind]);

  // Touch / mobile buttons
  const triggerAction = (action) => {
    unlockAudio();
    const s = stateRef.current;
    if (!s || hud.paused || overlay.kind) return;
    const tiger = s.tiger;
    const t = performance.now();
    if (action === "jump") {
      if (tiger.onGround) {
        tiger.vy = JUMP_VELOCITY;
        tiger.onGround = false;
        tiger.canDoubleJump = true;
        SFX.jump();
      } else if (tiger.canDoubleJump) {
        tiger.vy = JUMP_VELOCITY * 0.85;
        tiger.canDoubleJump = false;
        tiger.state = STATE.BACKFLIP;
        tiger.actionUntil = t + 500;
        SFX.backflip();
      }
    }
    if (action === "eat") {
      tiger.state = STATE.EAT;
      tiger.actionUntil = t + 300;
      SFX.eat();
    }
    if (action === "drink") {
      tiger.state = STATE.DRINK;
      tiger.actionUntil = t + 300;
      SFX.drink();
    }
    if (action === "roar") {
      tiger.state = STATE.ROAR;
      tiger.actionUntil = t + 700;
      SFX.roar();
    }
  };

  const computeStars = (s) => {
    const collected = s.meat + s.water;
    const hitsPenalty = s.hits;
    if (hitsPenalty === 0 && collected >= 6) return 3;
    if (hitsPenalty <= 1 && collected >= 4) return 2;
    return 1;
  };

  const onSaveName = () => {
    const n = playerName.trim();
    if (!n) {
      toast.error("Please enter your name first!");
      return;
    }
    localStorage.setItem("tiger_player_name", n);
    setAskName(false);
    SFX.click();
  };

  const handleNextLevel = () => {
    SFX.click();
    goToLevel(hud.levelIdx + 1);
  };

  const handleRestartLevel = () => {
    SFX.click();
    setHud((h) => ({
      ...h,
      lives: 5,
      timeLeft: LEVEL_DURATION_MS,
      paused: false,
    }));
    initLevel(hud.levelIdx);
    setOverlay({ kind: null });
    if (isMusicEnabled()) startMusic(LEVELS[hud.levelIdx].music);
  };

  const toggleMusic = () => {
    const next = !hud.musicOn;
    setHud((h) => ({ ...h, musicOn: next }));
    setMusicEnabled(next);
    if (next && !overlay.kind) {
      startMusic(LEVELS[hud.levelIdx].music);
    }
  };

  const togglePause = () => {
    setHud((h) => ({ ...h, paused: !h.paused }));
  };

  const currentLevel = LEVELS[hud.levelIdx];

  // Cleanup
  useEffect(() => {
    return () => stopMusic();
  }, []);

  if (askName) {
    return (
      <div
        className="mx-auto max-w-md rounded-3xl border-[4px] border-tiger-deep bg-white p-8 shadow-stickerLg"
        data-testid="name-modal"
      >
        <h2 className="font-heading text-3xl font-bold text-tiger-deep">
          What&apos;s your name? 🐯
        </h2>
        <p className="mt-2 font-body text-base text-tiger-deep">
          Your name will appear on your Tiger Hero certificate after each level.
        </p>
        <input
          type="text"
          value={playerName}
          onChange={(e) => setPlayerName(e.target.value)}
          placeholder="e.g. Romir"
          maxLength={20}
          className="mt-5 w-full rounded-2xl border-[3px] border-tiger-deep bg-tiger-cream px-4 py-3 font-body text-lg font-bold text-tiger-deep placeholder:text-tiger-deep/40 focus:outline-none focus:bg-white"
          data-testid="player-name-input"
        />
        <button
          onClick={onSaveName}
          className="mt-5 w-full rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-6 py-3 font-heading text-lg font-bold text-white shadow-sticker hover:-translate-y-0.5 hover:shadow-stickerLg transition-transform"
          data-testid="save-name-button"
        >
          Let&apos;s Play!
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-5xl" data-testid="game-container">
      {/* HUD */}
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3 rounded-2xl border-[3px] border-tiger-deep bg-white px-4 py-3 shadow-sticker">
        <div className="flex items-center gap-2" data-testid="lives-hud">
          {Array.from({ length: 5 }).map((_, i) => (
            <Heart
              key={i}
              className={`h-6 w-6 transition-all ${
                i < hud.lives ? "fill-tiger-orange text-tiger-orange" : "text-tiger-deep/20"
              }`}
            />
          ))}
        </div>
        <div className="flex items-center gap-3">
          <span
            className="rounded-full border-[3px] border-tiger-deep bg-sun px-3 py-0.5 font-heading text-sm font-bold text-tiger-deep"
            data-testid="level-hud"
          >
            {currentLevel.emoji} Level {hud.levelIdx + 1}: {currentLevel.name}
          </span>
          <span
            className="rounded-full border-[3px] border-tiger-deep bg-jungle-leaf px-3 py-0.5 font-heading text-sm font-bold text-white"
            data-testid="score-hud"
          >
            ⭐ {hud.score}
          </span>
          <span className="rounded-full border-[3px] border-tiger-deep bg-white px-3 py-0.5 font-heading text-sm font-bold text-tiger-deep" data-testid="time-hud">
            ⏱ {Math.ceil(hud.timeLeft / 1000)}s
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={toggleMusic}
            className="grid h-9 w-9 place-items-center rounded-full border-[3px] border-tiger-deep bg-white shadow-sticker hover:-translate-y-0.5 transition-transform"
            data-testid="toggle-music"
            aria-label="Toggle music"
          >
            {hud.musicOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
          </button>
          <button
            onClick={togglePause}
            className="grid h-9 w-9 place-items-center rounded-full border-[3px] border-tiger-deep bg-white shadow-sticker hover:-translate-y-0.5 transition-transform"
            data-testid="toggle-pause"
            aria-label="Toggle pause"
          >
            {hud.paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
          </button>
        </div>
      </div>

      {/* Canvas */}
      <div className="relative">
        <div className="game-frame overflow-hidden">
          <canvas
            ref={canvasRef}
            width={VIEW_W}
            height={VIEW_H}
            className="w-full h-auto block"
            style={{ aspectRatio: `${VIEW_W} / ${VIEW_H}` }}
            data-testid="game-canvas"
            onClick={() => triggerAction("jump")}
          />
        </div>

        {/* Overlays */}
        {overlay.kind === "start" && (
          <Overlay>
            <h2 className="font-heading text-4xl font-bold text-tiger-deep">
              🐯 Tiger Adventure
            </h2>
            <p className="mt-3 font-body text-lg text-tiger-deep">
              Hi <span className="font-bold text-tiger-orange">{playerName}</span>!
              Help the tiger escape the hunters across 10 levels!
            </p>
            <Controls />
            <button
              onClick={startGame}
              className="mt-6 rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-8 py-3 font-heading text-xl font-bold text-white shadow-stickerLg hover:-translate-y-0.5 transition-transform"
              data-testid="start-game-button"
            >
              ▶ Start Game
            </button>
          </Overlay>
        )}

        {overlay.kind === "levelComplete" && (
          <Overlay>
            <h2 className="font-heading text-4xl font-bold text-tiger-deep">
              🎉 Level Complete!
            </h2>
            <p className="mt-2 font-body text-lg text-tiger-deep">
              {overlay.levelName}
            </p>
            <div className="mt-4 flex justify-center gap-3">
              {[0, 1, 2].map((i) => (
                <Star
                  key={i}
                  className={`h-14 w-14 ${
                    i < overlay.stars
                      ? "fill-sun text-sun animate-pop-in"
                      : "text-tiger-deep/20"
                  }`}
                  style={{ animationDelay: `${i * 120}ms` }}
                />
              ))}
            </div>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setShowCertificate(true)}
                className="rounded-full border-[3px] border-tiger-deep bg-sun px-5 py-2 font-heading font-bold text-tiger-deep shadow-sticker hover:-translate-y-0.5 transition-transform"
                data-testid="get-certificate-button"
              >
                📸 Get Picture with Tiger!
              </button>
              <button
                onClick={handleNextLevel}
                className="rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-6 py-2 font-heading font-bold text-white shadow-sticker hover:-translate-y-0.5 transition-transform"
                data-testid="next-level-button"
              >
                Next Level →
              </button>
            </div>
          </Overlay>
        )}

        {overlay.kind === "gameOver" && (
          <Overlay>
            <h2 className="font-heading text-4xl font-bold text-red-600">
              💔 Game Over!
            </h2>
            <p className="mt-3 font-body text-lg text-tiger-deep">
              The hunters got the tiger 😿 Try again, hero!
            </p>
            <div className="mt-5 flex flex-wrap justify-center gap-3">
              <button
                onClick={handleRestartLevel}
                className="rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-6 py-2 font-heading font-bold text-white shadow-sticker hover:-translate-y-0.5 transition-transform"
                data-testid="restart-level-button"
              >
                Try this level again
              </button>
              <button
                onClick={startGame}
                className="rounded-full border-[3px] border-tiger-deep bg-white px-6 py-2 font-heading font-bold text-tiger-deep shadow-sticker hover:-translate-y-0.5 transition-transform"
                data-testid="restart-game-button"
              >
                Restart Game
              </button>
            </div>
          </Overlay>
        )}

        {overlay.kind === "finished" && (
          <Overlay>
            <h2 className="font-heading text-4xl font-bold text-tiger-deep">
              🏆 You Did It!
            </h2>
            <p className="mt-3 font-body text-lg text-tiger-deep">
              You finished all 10 levels and saved the tigers!
            </p>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <button
                onClick={() => setShowCertificate(true)}
                className="rounded-full border-[3px] border-tiger-deep bg-sun px-5 py-2 font-heading font-bold text-tiger-deep shadow-sticker hover:-translate-y-0.5 transition-transform"
                data-testid="finish-certificate-button"
              >
                📸 Final Certificate
              </button>
              <button
                onClick={startGame}
                className="rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-6 py-2 font-heading font-bold text-white shadow-sticker hover:-translate-y-0.5 transition-transform"
                data-testid="play-again-button"
              >
                Play Again
              </button>
            </div>
          </Overlay>
        )}

        {hud.paused && !overlay.kind && (
          <Overlay>
            <h2 className="font-heading text-4xl font-bold text-tiger-deep">
              ⏸ Paused
            </h2>
            <button
              onClick={togglePause}
              className="mt-4 rounded-full border-[3px] border-tiger-deep bg-tiger-orange px-6 py-2 font-heading font-bold text-white shadow-sticker"
              data-testid="resume-button"
            >
              Resume
            </button>
          </Overlay>
        )}
      </div>

      {/* Mobile / touch controls */}
      <div className="mt-4 grid grid-cols-4 gap-2 sm:gap-3" data-testid="touch-controls">
        <TouchBtn label="Jump" onPress={() => triggerAction("jump")} testid="btn-jump" />
        <TouchBtn label="Eat" onPress={() => triggerAction("eat")} testid="btn-eat" />
        <TouchBtn label="Drink" onPress={() => triggerAction("drink")} testid="btn-drink" />
        <TouchBtn label="ROAR!" onPress={() => triggerAction("roar")} testid="btn-roar" big />
      </div>

      {/* Controls helper */}
      <div className="mt-4 rounded-2xl border-[3px] border-tiger-deep bg-tiger-cream px-4 py-3 text-center font-body text-sm font-bold text-tiger-deep shadow-sticker">
        Keyboard: <span className="text-tiger-orange">↑/Space</span> Jump · double-tap = Backflip
        · <span className="text-tiger-orange">E</span> Eat ·{" "}
        <span className="text-tiger-orange">D</span> Drink ·{" "}
        <span className="text-tiger-orange">R</span> Roar (scares hunters!) ·{" "}
        <span className="text-tiger-orange">P</span> Pause
      </div>

      {showCertificate && (
        <CertificateModal
          name={playerName}
          levelName={
            overlay.kind === "finished"
              ? "All 10 Levels"
              : lastResult.levelName || currentLevel.name
          }
          stars={overlay.kind === "finished" ? 3 : lastResult.stars}
          onClose={() => setShowCertificate(false)}
        />
      )}
    </div>
  );
}

function Overlay({ children }) {
  return (
    <div
      className="absolute inset-0 grid place-items-center bg-tiger-deep/70 rounded-[24px] animate-fade-up"
      data-testid="game-overlay"
    >
      <div className="mx-4 max-w-md rounded-3xl border-[4px] border-tiger-deep bg-white p-8 text-center shadow-stickerLg">
        {children}
      </div>
    </div>
  );
}

function Controls() {
  return (
    <div className="mt-5 grid grid-cols-2 gap-2 text-left text-sm font-body font-bold text-tiger-deep">
      <span>↑ / Space — Jump</span>
      <span>↑↑ — Backflip</span>
      <span>E — Eat 🍖</span>
      <span>D — Drink 💧</span>
      <span>R — Roar 🦁</span>
      <span>P — Pause</span>
    </div>
  );
}

function TouchBtn({ label, onPress, testid, big }) {
  return (
    <button
      onClick={onPress}
      className={`rounded-2xl border-[3px] border-tiger-deep ${
        big ? "bg-tiger-orange text-white" : "bg-white text-tiger-deep"
      } py-3 sm:py-4 font-heading text-base sm:text-lg font-bold shadow-sticker hover:-translate-y-0.5 active:translate-y-0 transition-transform`}
      data-testid={testid}
    >
      {label}
    </button>
  );
}
